/**
 * Shipping Cost Cache Utility
 * 
 * Caches shipping cost for 30 days to avoid unnecessary API calls.
 * The backend updates shipping cost monthly via cron job, so we only
 * need to fetch it once per month per user's browser.
 */

const SHIPPING_COST_KEY = 'shippingCost';
const SHIPPING_COST_TIMESTAMP_KEY = 'shippingCostTimestamp';
const CACHE_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

/**
 * Get cached shipping cost if it exists and is still valid
 * @returns {number|null} The cached shipping cost or null if not cached/expired
 */
export const getCachedShippingCost = () => {
  try {
    const cachedValue = localStorage.getItem(SHIPPING_COST_KEY);
    const cachedTimestamp = localStorage.getItem(SHIPPING_COST_TIMESTAMP_KEY);
    
    if (!cachedValue || !cachedTimestamp) {
      return null;
    }
    
    const timestamp = parseInt(cachedTimestamp, 10);
    const now = Date.now();
    const age = now - timestamp;
    
    // Check if cache is still valid (less than 30 days old)
    if (age > CACHE_DURATION_MS) {
      // Cache expired, remove it
      localStorage.removeItem(SHIPPING_COST_KEY);
      localStorage.removeItem(SHIPPING_COST_TIMESTAMP_KEY);
      return null;
    }
    
    return parseFloat(cachedValue);
  } catch (error) {
    console.error('Error reading cached shipping cost:', error);
    return null;
  }
};

/**
 * Save shipping cost to cache with current timestamp
 * @param {number} shippingCost - The shipping cost to cache
 */
export const setCachedShippingCost = (shippingCost) => {
  try {
    localStorage.setItem(SHIPPING_COST_KEY, shippingCost.toString());
    localStorage.setItem(SHIPPING_COST_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error caching shipping cost:', error);
  }
};

/**
 * Check if cached shipping cost exists and is valid
 * @returns {boolean} True if valid cache exists, false otherwise
 */
export const hasValidCache = () => {
  return getCachedShippingCost() !== null;
};

/**
 * Clear the shipping cost cache (useful for testing or manual refresh)
 */
export const clearShippingCostCache = () => {
  try {
    localStorage.removeItem(SHIPPING_COST_KEY);
    localStorage.removeItem(SHIPPING_COST_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Error clearing shipping cost cache:', error);
  }
};

/**
 * Get cache age in days
 * @returns {number|null} Age in days or null if no cache exists
 */
export const getCacheAge = () => {
  try {
    const cachedTimestamp = localStorage.getItem(SHIPPING_COST_TIMESTAMP_KEY);
    if (!cachedTimestamp) {
      return null;
    }
    
    const timestamp = parseInt(cachedTimestamp, 10);
    const now = Date.now();
    const ageMs = now - timestamp;
    return Math.floor(ageMs / (24 * 60 * 60 * 1000)); // Convert to days
  } catch (error) {
    console.error('Error getting cache age:', error);
    return null;
  }
};



